import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminGet, adminPatch, adminDelete } from "../../lib/admin-api";
import { Search, ChevronLeft, ChevronRight, Building2, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string | null;
  plan: string;
  isActive: boolean;
  verificationStatus: string;
  createdAt: string;
  _count: { locations: number; users: number };
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [page, setPage] = useState(1);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), pageSize: "25" });
    if (search) qs.set("search", search);
    if (pendingOnly) qs.set("verificationStatus", "PENDING");
    const res = await adminGet<Tenant[]>(`/tenants?${qs}`);
    if (res.success && res.data) {
      setTenants(res.data);
      setPagination(res.pagination);
    }
    setLoading(false);
  }, [page, search, pendingOnly]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  async function toggleActive(tenant: Tenant) {
    await adminPatch(`/tenants/${tenant.id}`, { isActive: !tenant.isActive });
    fetchTenants();
  }

  async function deleteTenant(tenant: Tenant) {
    const confirmed = window.confirm(
      `Permanently delete "${tenant.name}"?\n\n` +
        "This removes the tenant and ALL its users, locations and check-in records. " +
        "This cannot be undone.",
    );
    if (!confirmed) return;
    const res = await adminDelete(`/tenants/${tenant.id}`);
    if (!res.success) {
      window.alert(res.error?.message || "Failed to delete tenant.");
      return;
    }
    fetchTenants();
  }

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6 font-display">All Tenants</h1>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search by name, email, or slug..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => {
            setPendingOnly((v) => !v);
            setPage(1);
          }}
          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
            pendingOnly
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
          }`}
        >
          Pending verification
        </button>
        {pagination && (
          <span className="text-sm text-stone-500">
            {pagination.total} tenant{pagination.total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-500">Loading...</div>
        ) : tenants.length === 0 ? (
          <div className="p-8 text-center text-stone-500">No tenants found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 bg-stone-50 border-b border-stone-200">
                  <th className="px-4 py-3 font-medium">Tenant</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium text-center">Locations</th>
                  <th className="px-4 py-3 font-medium text-center">Users</th>
                  <th className="px-4 py-3 font-medium">Registered</th>
                  <th className="px-4 py-3 font-medium text-center">Verification</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                  <th className="px-4 py-3 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-stone-50 hover:bg-stone-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/tenants/${t.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-stone-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-stone-900">{t.name}</p>
                          <p className="text-xs text-stone-400">{t.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{t.contactEmail}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.plan === "ENTERPRISE" ? "bg-purple-50 text-purple-700" : "bg-stone-100 text-stone-600"
                      }`}>
                        {t.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-stone-600">{t._count.locations}</td>
                    <td className="px-4 py-3 text-center text-stone-600">{t._count.users}</td>
                    <td className="px-4 py-3 text-stone-500">{new Date(t.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.verificationStatus === "APPROVED"
                          ? "bg-green-50 text-green-700"
                          : t.verificationStatus === "REJECTED"
                            ? "bg-red-50 text-red-700"
                            : "bg-amber-50 text-amber-700"
                      }`}>
                        {t.verificationStatus === "APPROVED"
                          ? "Verified"
                          : t.verificationStatus === "REJECTED"
                            ? "Rejected"
                            : "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActive(t);
                        }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                          t.isActive
                            ? "text-green-700 hover:bg-green-50"
                            : "text-red-600 hover:bg-red-50"
                        }`}
                        title={t.isActive ? "Click to deactivate" : "Click to activate"}
                      >
                        {t.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                        {t.isActive ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTenant(t);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                        title="Permanently delete this tenant"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200">
            <span className="text-sm text-stone-500">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
